import React, { useMemo, useState } from 'react'
import { gradeColor, SLOPE_LEGEND, START_PIN_COLOR, END_PIN_COLOR } from '../../../../../lib/routeDocument'
import { metersToDisplayDistance, metersToElev, distanceUnit, elevUnit } from '../../../../../lib/trackStats'

const W = 1104
const H = 128
let gradientSeq = 0

const num = n => Math.round(n).toLocaleString('en-US')

/**
 * Slope-coloured elevation profile for a route.
 * - compact (build/edit): 84px, waypoint ticks with distance labels, "X max".
 * - full (view): 122px with y-axis labels, quarter-distance x labels, a scrub tooltip
 *   and the SLOPE % legend.
 * profile: [{distM, elevM, gradePct}]; waypointDists: cumulative m per waypoint.
 * onHover(index|null) moves the builder's map marker along the route.
 */
const RouteProfile = ({ profile, waypointDists = [], units, compact = false, color, onHover }) => {
  const [hover, setHover] = useState(null)
  const [ids] = useState(() => { gradientSeq++; return { fade: `rbFade${gradientSeq}`, grade: `rbGrade${gradientSeq}` } })

  const geo = useMemo(() => {
    if (!profile || profile.length < 2) return null
    const total = profile[profile.length - 1].distM || 1
    let lo = Infinity
    let hi = -Infinity
    profile.forEach(p => { if (p.elevM < lo) lo = p.elevM; if (p.elevM > hi) hi = p.elevM })
    const pad = Math.max(4, (hi - lo) * 0.08)
    lo -= pad
    hi += pad
    const yOf = e => H - (e - lo) / (hi - lo || 1) * H
    let el = ''
    const stops = []
    const every = Math.max(1, Math.floor(profile.length / 160))
    profile.forEach((p, i) => {
      el += `${i ? 'L' : 'M'}${(p.distM / total * W).toFixed(1)},${yOf(p.elevM).toFixed(1)}`
      if (i % every === 0) stops.push({ offset: `${(p.distM / total * 100).toFixed(2)}%`, color: gradeColor(p.gradePct) })
    })
    return { total, lo, hi, yOf, el, ar: `${el}L${W},${H}L0,${H}Z`, stops, max: hi - pad }
  }, [profile])

  if (!geo) return null
  const height = compact ? 84 : 122
  const n = waypointDists.length
  const yPct = e => (geo.yOf(e) / H * 100).toFixed(2)
  const elevAt = (d) => {
    // Linear scan is fine: profiles are a few thousand vertices at most.
    for (let i = 1; i < profile.length; i++) {
      if (profile[i].distM >= d) {
        const a = profile[i - 1]
        const b = profile[i]
        const t = b.distM > a.distM ? (d - a.distM) / (b.distM - a.distM) : 0
        return a.elevM + (b.elevM - a.elevM) * t
      }
    }
    return profile[profile.length - 1].elevM
  }
  const marks = waypointDists.map((d, i) => {
    const end = i === 0 || i === n - 1
    return {
      xPct: (d / geo.total * 100).toFixed(2),
      yPct: yPct(elevAt(d)),
      fill: i === 0 ? START_PIN_COLOR : i === n - 1 ? END_PIN_COLOR : '#fff',
      size: end ? 11 : 8,
      line: end ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.16)'
    }
  })

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect()
    const f = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width))
    const d = f * geo.total
    let idx = 0
    while (idx < profile.length - 1 && profile[idx].distM < d) idx++
    setHover(idx)
    if (onHover) onHover(idx)
  }
  const onLeave = () => { setHover(null); if (onHover) onHover(null) }

  const dist = m => metersToDisplayDistance(m, units).toFixed(2)
  const hp = hover != null ? profile[hover] : null
  const hxPct = hp ? hp.distM / geo.total * 100 : 0

  const plot = (
    <div className='rb-prof-plot' style={{ height }} onMouseMove={onMove} onMouseLeave={onLeave}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio='none' style={{ height }}>
        <defs>
          <linearGradient id={ids.fade} x1='0' y1='0' x2='0' y2='1'>
            <stop offset='0%' stopColor='rgba(158,204,59,0.30)' />
            <stop offset='100%' stopColor='rgba(158,204,59,0.03)' />
          </linearGradient>
          <linearGradient id={ids.grade} x1='0' y1='0' x2={W} y2='0' gradientUnits='userSpaceOnUse'>
            {geo.stops.map((s, i) => <stop key={i} offset={s.offset} stopColor={s.color} />)}
          </linearGradient>
        </defs>
        <path d={geo.ar} fill={`url(#${ids.fade})`} />
        <path d={geo.el} fill='none' stroke={`url(#${ids.grade})`} strokeWidth='2.5' strokeLinejoin='round' vectorEffect='non-scaling-stroke' />
      </svg>
      {marks.map((m, i) => <div key={`l${i}`} className='rb-prof-wpline' style={{ left: `${m.xPct}%`, background: m.line }} />)}
      {marks.map((m, i) => (
        <div key={`d${i}`} className='rb-prof-wpdot' style={{
          left: `${m.xPct}%`, top: `${m.yPct}%`, width: m.size, height: m.size,
          marginLeft: -m.size / 2, marginTop: -m.size / 2, background: m.fill
        }} />
      ))}
      {hp && (
        <>
          <div className='rb-prof-hoverline' style={{ left: `${hxPct}%`, height }} />
          <div className='rb-prof-hoverdot' style={{ left: `${hxPct}%`, top: `${yPct(hp.elevM)}%`, background: color }} />
          {!compact && (
            <div className='rb-prof-tip' style={{ left: `${Math.max(0, Math.min(52, hxPct - 12))}%` }}>
              <b>{dist(hp.distM)} {distanceUnit(units)}</b>
              <span>{num(metersToElev(hp.elevM, units))} {elevUnit(units)}</span>
              <span>{hp.gradePct > 0 ? '+' : ''}{hp.gradePct.toFixed(0)}% grade</span>
            </div>
          )}
        </>
      )}
    </div>
  )

  if (compact) {
    // Waypoint distance ticks; interior labels only when they won't collide.
    let last = -99
    const ticks = waypointDists.map((d, i) => {
      const pct = d / geo.total * 100
      const show = i === 0 || i === n - 1 || pct - last > 18
      if (show) last = pct
      return { pct, label: dist(d), show }
    })
    return (
      <div className='rb-card rb-prof rb-prof-compact'>
        <div className='rb-prof-head'>
          <span className='rb-eyebrow'>ELEVATION PROFILE</span>
          <span className='rb-prof-meta'>{num(metersToElev(geo.max, units))} {elevUnit(units)} max</span>
        </div>
        {plot}
        <div className='rb-prof-ticks'>
          {ticks.map((t, i) => (
            <div key={i} className='rb-prof-tick' style={{ left: `${t.pct.toFixed(2)}%` }}>
              <div />
              <span style={{ opacity: t.show ? 1 : 0 }}>{t.label}</span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const q = geo.total / 4
  const yLabels = [1, 0.5, 0].map(f => num(metersToElev(geo.lo + (geo.hi - geo.lo) * f, units)))
  return (
    <div className='rb-prof rb-prof-full'>
      <div className='rb-eyebrow rb-prof-title'>ELEVATION PROFILE</div>
      <div className='rb-prof-body'>
        <div className='rb-prof-yaxis' style={{ height }}>{yLabels.map((l, i) => <span key={i}>{l}</span>)}</div>
        <div className='rb-prof-main'>
          <div className='rb-prof-axes'>{plot}</div>
          <div className='rb-prof-xaxis'>
            <span>0 {distanceUnit(units)}</span><span>{dist(q)}</span><span>{dist(q * 2)}</span><span>{dist(q * 3)}</span>
            <span>{dist(geo.total)} {distanceUnit(units)}</span>
          </div>
        </div>
      </div>
      <div className='rb-slope-legend'>
        <span className='rb-eyebrow'>SLOPE %</span>
        {SLOPE_LEGEND.map(s => (
          <span key={s.label} className='rb-slope-item'><i style={{ background: s.color }} />{s.label}</span>
        ))}
      </div>
    </div>
  )
}

export default RouteProfile
